import { AxiosResponse } from "axios";
import instance from "./axiosInstance";

export interface SizeChartRequest {
  Brand: string;
  Name: number;
  Gender: string;
  Category: string;
  Size: string;
  OptionalType: string;
  Sizes: Array<{
    Size: string;
    EuropeSize: string;
    [measurement: string]: number | [number, number] | string;
  }>;
}

export interface SizeChartResponse {
  ok: boolean;
  message?: string;
  data?: any;
  status_code?: number;
}

export interface SuggestedDataResponse {
  ok: boolean;
  message: string;
  data: {
    Category: string[];
    Gender: string[];
    Size: string[];
  };
  status_code: number;
}

export interface BrandChartResponse {
  ok: boolean;
  message: string;
  data: any;
  status_code: number;
}

export interface UpdateChartRequest {
  Brand: string;
  ChartData: any;
}

export interface DeleteChartRequest {
  Brand: string;
}

export const getSuggestedData = async (accessToken: string): Promise<SuggestedDataResponse> => {
  try {
    const response: AxiosResponse<SuggestedDataResponse> = await instance.get("/get_suggested_data", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching suggested data:", error);
    return {
      ok: false,
      message: "Failed to fetch suggested data",
      data: {
        Category: [],
        Gender: [],
        Size: [],
      },
      status_code: 500,
    };
  }
};

export const submitSizeChart = async (accessToken: string, data: SizeChartRequest): Promise<SizeChartResponse> => {
  try {
    const response: AxiosResponse<SizeChartResponse> = await instance.post("/api/v1/services/size_chart", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error submitting size chart:", error);

    if (error.response) {
      return {
        ok: false,
        message: error.response.data?.message || "Server error occurred",
        data: error.response.data?.data,
        status_code: error.response.data?.status_code,
      };
    } else if (error.request) {
      return {
        ok: false,
        message: "Network error - please check your connection",
        status_code: -1,
      };
    } else {
      return {
        ok: false,
        message: error.message || "Unknown error occurred",
        status_code: -1,
      };
    }
  }
};

export const getBrandChart = async (accessToken: string, brandId: string): Promise<BrandChartResponse> => {
  try {
    const response: AxiosResponse<BrandChartResponse> = await instance.post(
      "/api/v1/services/show_brand_chart",
      { Brand: brandId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching brand chart:", error);
    return {
      ok: false,
      message: "Failed to fetch brand chart",
      data: null,
      status_code: 500,
    };
  }
};

export const updateBrandChart = async (accessToken: string, data: UpdateChartRequest): Promise<SizeChartResponse> => {
  try {
    const response: AxiosResponse<SizeChartResponse> = await instance.post("/api/v1/services/update_brand_chart", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error updating brand chart:", error);
    return {
      ok: false,
      message: "Failed to update brand chart",
      status_code: 500,
    };
  }
};

export const deleteBrandChart = async (accessToken: string, data: DeleteChartRequest): Promise<SizeChartResponse> => {
  try {
    const response: AxiosResponse<SizeChartResponse> = await instance.post("/api/v1/services/delete_brand_chart", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error deleting brand chart:", error);
    return {
      ok: false,
      message: "Failed to delete brand chart",
      status_code: 500,
    };
  }
};
